import { apiClient } from "../API/axiosInstance";
import axios from "axios";
import { CONFIG } from "../config";
import { logger } from "../utils/logger";
import { parseJsonSafe } from "../utils/json";

export class DeviceService {
  static async changeDeviceName(device_id, device_name) {
    try {
      let response = await apiClient.put(`/device/${device_id}/name`, {
        name: device_name,
      });
      return response;
    } catch (error) {
      logger.warn(error);
      throw error;
    }
  }

  static async fetchDevices(org_id) {
    if (!org_id) return [];
    const response = await apiClient.get(`/organisation/${org_id}/devices`);
    return response.data || [];
  }

  static async fetchDevice(deviceID) {
    if (!deviceID) return null;
    const response = await apiClient.get(`/device/${deviceID}`);
    return response.data || null;
  }

  static async fetchDeviceHistory(deviceID, startDate, endDate) {
    if (!deviceID || startDate === undefined || endDate === undefined) {
      return [];
    }

    const response = await apiClient.get(
      `/frame/${deviceID}/${startDate}/${endDate}`,
      { timeout: 40000 } // 40 seconds for large history fetches
    );

    if (response.data) {
      // Sort history by time_created oldest to newest
      const data = response.data.sort((a, b) => {
        return new Date(a.time_created) - new Date(b.time_created);
      });
      return data;
    }

    return [];
  }

  static async fetchDeviceAlerts(deviceID, startDate, endDate) {
    if (!deviceID) return [];

    const response = await apiClient.get(
      `/device/${deviceID}/alert?start_time=${startDate}&end_time=${endDate}`
    );

    return response.data || [];
  }

  static async fetchDeviceBrands() {
    const response = await apiClient.get("/device/brands");
    return response.data || [];
  }

  static async fetchDeviceProfile(deviceID) {
    if (!deviceID) return null;

    const response = await apiClient.get(`/device/${deviceID}/profile`);

    if (response.data && response.data.profile) {
      const parsed = parseJsonSafe(response.data.profile, null);
      if (!parsed) {
        logger.warn("error parsing profile json");
        return null;
      }

      return parsed;
    }

    return null;
  }

  static async updateDeviceXY(deviceID, x, y) {
    if (!deviceID) {
      throw new Error("Device ID is required");
    }

    const data = { x, y };
    const response = await apiClient.put(`/device/${deviceID}/xy`, data);
    return response;
  }

  static async claimDevice(claimCode, organisationId) {
    const response = await apiClient.post("/device/claim", {
      claimCode,
      organisation_id: organisationId,
    });
    return response;
  }

  static async selfHostDevice(deviceData) {
    const response = await apiClient.post("/device/self-host", deviceData);
    return response;
  }

  static async deleteDevice(deviceId) {
    const response = await apiClient.delete(`/device/${deviceId}`);
    return response;
  }

  static async transferDevice(deviceId, organisationId) {
    const response = await apiClient.put(`/device/${deviceId}/transfer`, {
      organisation_id: organisationId,
    });
    return response;
  }

  static async updateDeviceProfile({ deviceId, profile }) {
    const data = {
      profile: JSON.stringify(profile),
      device_id: deviceId,
    };
    return await apiClient.post(`/device/${deviceId}/profile`, data);
  }

  static async addDeviceManually({
    organisationId,
    devEui,
    appEui,
    appKey,
    lnsId,
    region,
    deviceBrandId,
    name,
  }) {
    return await apiClient.post("/device/manual-add", {
      organisation_id: organisationId,
      dev_eui: devEui,
      app_eui: appEui,
      app_key: appKey,
      lns_id: lnsId,
      region: region,
      device_brand: deviceBrandId,
      name: name,
    });
  }

  static async fetchLNS() {
    const response = await apiClient.get("/device/lns/");
    return response.data;
  }

  static async sendDownlink({ deviceId, command, port }) {
    return await apiClient.post(`/device/${deviceId}/downlink`, {
      command: command,
      port: port,
    });
  }

  static async sendCat1mDownlink({ deviceId, command }) {
    return await apiClient.post(`/device/${deviceId}/downlink/cat1m`, {
      device_id: deviceId,
      command: command,
    });
  }

  static async deleteFrame({ deviceId, fcnt, timeCreated }) {
    return await apiClient.delete(`/frame/${deviceId}/${fcnt}/${timeCreated}`);
  }

  static async shareDevice(deviceId) {
    const response = await apiClient.get(`/device/${deviceId}/share`);
    return response.data;
  }

  // Share-specific endpoints that don't require authentication - use axios directly
  static async fetchSharedDevice(shareId) {
    if (!shareId) return null;
    const response = await axios.get(
      CONFIG.API_URL + `/device/share/info/${shareId}`
    );
    return response.data || null;
  }

  static async fetchSharedDeviceHistory(shareId, startDate, endDate) {
    if (!shareId || startDate === undefined || endDate === undefined) {
      return [];
    }

    const response = await axios.get(
      CONFIG.API_URL + `/device/share/${shareId}/${startDate}/${endDate}`
    );

    if (response.data) {
      // Sort history by time_created oldest to newest
      const data = response.data.sort((a, b) => {
        return new Date(a.time_created) - new Date(b.time_created);
      });
      return data;
    }

    return [];
  }

  static async fetchSharedDeviceProfile(shareId) {
    if (!shareId) return null;
    const response = await axios.get(
      CONFIG.API_URL + `/device/share/profile/${shareId}`
    );
    return response.data || null;
  }

  static async fetchDevicesAtLimit() {
    const response = await apiClient.get(`/device/limit`);
    return response.data;
  }

  static async fetchDeviceHistoryByDays(deviceId, days) {
    if (!deviceId || days === undefined) return [];
    const response = await apiClient.get(`/frame/days/${deviceId}/${days}`);
    return response.data || [];
  }
}
